/* eslint-disable no-unused-vars */
import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Context } from '@front10/helpers/dist/Context';
import useStyles from '@front10/helpers/dist/UseStyles';
import Box from '../../elements/Box';
import Col from '../../elements/Col';
import Dot from './atoms/Dot';
import ActionButton from './atoms/ActionButton';
import Text from '../../elements/Text';
import ElasticCarousel, { consts } from 'react-elastic-carousel';
import { useSwipeable } from 'react-swipeable';

function useCarouselDragging() {
  const [dragging, setDragging] = useState(false);

  const handleBeforeChange = useCallback(() => {
    setDragging(true);
  }, [setDragging]);

  const handleAfterChange = useCallback(() => {
    setDragging(false);
  }, [setDragging]);

  const handleOnItemClick = useCallback(
    (e) => {
      if (dragging) {
        e.stopPropagation();
      }
    },
    [dragging]
  );

  return {
    handleBeforeChange,
    handleAfterChange,
    handleOnItemClick
  };
}

// Apply settings for current active dot
const activeDotSettings = (index, dotActive, dotSettings) =>
  index === dotActive ? dotSettings.dotActiveSetting : {};

// Length of dots array
const dotsLength = ({ length, elementsToShow, elementsToScroll }) => {
  const coef = (length - elementsToShow) % elementsToScroll === 0 ? 1 : 2;
  return Math.trunc((length - elementsToShow) / elementsToScroll) + coef;
};

// Apply setting for dots in each side of the carousel
const sideDotsSettings = (
  i,
  dotsVisible,
  dotSettings,
  itemsData,
  elementsToShow,
  elementsToScroll
) => {
  const { from, to } = dotsVisible;
  const length = dotsLength({ length: itemsData.length, elementsToShow, elementsToScroll });
  const startPoint = 0;
  const endPoint = length - 1;
  return (i === from && i !== startPoint) || (i === to && i !== endPoint)
    ? dotSettings.sideDots
    : {};
};

const getElementsToShow = ({ widthCardAuto, itemsData, elementsToShow }) =>
  widthCardAuto && itemsData.length < elementsToShow ? itemsData.length : elementsToShow;

// Hooks for manage element to show
const useElementsToShow = ({ widthCardAuto, itemsData, elementsToShow }) => {
  return React.useState(getElementsToShow({ widthCardAuto, itemsData, elementsToShow }));
};

const getElementsToScroll = ({ widthCardAuto, itemsData, elementsToScroll }) =>
  widthCardAuto && itemsData.length < elementsToScroll ? itemsData.length : elementsToScroll;

// Hooks for manage element to scroll
const useElementsToScroll = ({ widthCardAuto, itemsData, elementsToScroll }) =>
  React.useState(getElementsToScroll({ widthCardAuto, itemsData, elementsToScroll }));

// Hook for work with dots
const useDots = ({ itemsData, elementsToShow, elementsToScroll, maxNumberDots }) => {
  const [dotActive, setDotActive] = React.useState(0);
  const [dotsVisible, setDotsVisible] = React.useState({
    from: 0,
    to: maxNumberDots - 1
  });
  return [dotActive, dotsVisible, setDotActive, setDotsVisible];
};

// Define if dot is visible
const isDotVisible = (i, dotsVisible) => {
  let result = false;
  result = i >= dotsVisible.from && i <= dotsVisible.to;
  return result;
};

const Carousel = (props) => {
  const {
    showDots,
    showNavigation,
    autoplay,
    speed,
    itemsData,
    lazyLoad,
    prevButtonSettings,
    nextButtonSettings,
    dotsContainerSettings,
    reelSettings,
    dotSettings,
    colSettings,
    itemSettings,
    ItemComponentInstance,
    maxNumberDots,
    showArrowsWhenDisabled,
    cardHeight,
    tracking,
    recProps,
    autoAdjustViewOfComponent,
    paramAutoAdjustViewOfComponent
  } = props;

  const defaultSetting = {
    itemsToShow: props.elementsToShow,
    itemsToScroll: props.elementsToScroll
  };

  const {
    deviceViewPortSize = {
      mobile: { width: 0 },
      tablet: { width: 481 },
      desktop: { width: 1025 }
    },
    mobileViewParams = { ...defaultSetting },
    tabletViewParams = { ...defaultSetting },
    desktopViewParams = { ...defaultSetting }
  } = paramAutoAdjustViewOfComponent;

  const breakPoints = autoAdjustViewOfComponent
    ? [
        { width: deviceViewPortSize.mobile.width, ...mobileViewParams },
        { width: deviceViewPortSize.tablet.width, ...tabletViewParams },
        { width: deviceViewPortSize.desktop.width, ...desktopViewParams }
      ]
    : undefined;

  const {
    // eslint-disable-next-line no-unused-vars
    handleAfterChange: handleDraggingAfterChange,
    // eslint-disable-next-line no-unused-vars
    handleBeforeChange: handleDraggingBeforeChange,
    handleOnItemClick
  } = useCarouselDragging();

  const { direction, theme } = React.useContext(Context);
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [currentBtnActive, setCurrentBtnActive] = React.useState('');
  const elasticCarouselRef = React.useRef(null);
  const [breakPointActive, setBreakPointActive] = React.useState(0);
  const { translate } = React.useContext(Context);
  const [elementsToShow, setElementsToShow] = useElementsToShow(props);
  const [elementsToScroll, setElementsToScroll] = useElementsToScroll(props);

  const [className, style] = useStyles(props, { direction, theme });
  const [dotActive, dotsVisible, setDotActive, setDotsVisible] = useDots({
    itemsData,
    elementsToShow,
    elementsToScroll,
    maxNumberDots
  });

  const isKeyboardNavigator = React.useRef(false);
  const arrayRef = React.useRef([]);

  const emptyCards =
    Number.isInteger(elementsToShow) && itemsData.length % elementsToShow !== 0
      ? Math.floor(elementsToShow - (itemsData.length % elementsToShow))
      : 0;
  useMemo(() => {
    if (emptyCards && itemsData.length > elementsToShow) {
      Array(emptyCards)
        .fill()
        .forEach(() => {
          itemsData.push({ noData: true });
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emptyCards, itemsData, elementsToShow, breakPointActive]);

  const itemsSize = useMemo(() => {
    return itemsData.length;
  }, [itemsData]);

  const showPagination = useMemo(() => {
    if (showDots) {
      if (itemsData.length > elementsToShow) {
        return true;
      } else {
        return false;
      }
    }
    return false;
  }, [itemsData, elementsToShow, showDots]);

  // When change currentIndex set focus in firs card visible
  React.useEffect(() => {
    if (isKeyboardNavigator.current && arrayRef.current[currentIndex]) {
      if (
        (currentBtnActive === 'prev' && direction === 'ltr') ||
        (currentBtnActive === 'next' && direction === 'rtl')
      ) {
        arrayRef.current[currentIndex + elementsToShow - 1].focus();
      } else {
        arrayRef.current[currentIndex].focus();
      }
    }
    isKeyboardNavigator.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrayRef, currentIndex]);

  const handleChange = (index, pageIndex) => {
    if (currentIndex !== index) {
      const { from, to } = dotsVisible;
      const newRange = { ...dotsVisible };
      let newDotActive = 0;
      if (index !== 0) {
        newDotActive = pageIndex;
      }
      const length = dotsLength({ length: itemsData.length, elementsToShow, elementsToScroll });
      const startPoint = from;
      const endPoint = to;
      if (index > currentIndex && newDotActive === endPoint && newDotActive !== length - 1) {
        newRange.from = from + 1;
        newRange.to = to + 1;
      } else if (index < currentIndex && newDotActive === startPoint && newDotActive !== 0) {
        newRange.from = from - 1;
        newRange.to = to - 1;
      }
      if (index > currentIndex && newDotActive > endPoint) {
        newRange.from = from + 1 + (newDotActive - endPoint);
        newRange.to = newDotActive + 1;
      } else if (index < currentIndex && newDotActive < startPoint) {
        newRange.from = newDotActive - 1;
        newRange.to = to - 1 - (startPoint - newDotActive);
      }
      setDotActive(newDotActive);
      setDotsVisible(newRange);
      setCurrentIndex(index);
    }
  };

  // Define if user is navigate by keyboard
  const activeKeyboard = () => {
    isKeyboardNavigator.current = true;
  };

  // // eslint-disable-next-line react-hooks/exhaustive-deps
  const emptyCardsLenght = useMemo(() => itemsData.filter((item) => item.noData).length, [
    itemsData
  ]);

  React.useEffect(() => {
    const newRange = { from: 0, to: maxNumberDots - 1 };
    setDotsVisible(newRange);
    setDotActive(0);
    setCurrentIndex(0);
    elasticCarouselRef.current.goTo(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsData]);

  React.useEffect(() => {
    setElementsToShow(
      autoAdjustViewOfComponent ? breakPointActive.itemsToShow : getElementsToShow(props)
    );
    setElementsToScroll(
      autoAdjustViewOfComponent ? breakPointActive.itemsToScroll : getElementsToScroll(props)
    );
    if (
      autoAdjustViewOfComponent &&
      breakPointActive &&
      breakPointActive.itemsToShow !== elementsToShow
    ) {
      Array(emptyCardsLenght)
        .fill()
        .forEach(() => {
          itemsData.pop();
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    itemsData,
    setElementsToShow,
    setElementsToScroll,
    elementsToShow,
    elementsToScroll,
    breakPointActive,
    props
  ]);

  // Calculate height of the cards, prev and next buttons
  const newHeight = cardHeight || '100%';
  const buttonsHeight = { height: newHeight };

  const length = dotsLength({ length: itemsData.length, elementsToShow, elementsToScroll });

  // Go to next slide and if rtl go to back slide
  const handleNext = (e, withKeyboard) => {
    //elasticCarouselRef.current.onNextStart();
    if (elasticCarouselRef.current && !withKeyboard) {
      direction === 'ltr' ? setCurrentBtnActive('next') : setCurrentBtnActive('prev');
      elasticCarouselRef.current.slideNext();
      tracking({ type: 'CAROUSEL_ARROW_RIGHT' });
    }
    if (withKeyboard) {
      activeKeyboard();
    }
  };

  // Go to back slide and if rtl go to next slide
  const handlePrev = (e, withKeyboard) => {
    if (elasticCarouselRef.current && !withKeyboard) {
      direction === 'ltr' ? setCurrentBtnActive('prev') : setCurrentBtnActive('next');
      elasticCarouselRef.current.slidePrev();
      tracking({ type: 'CAROUSEL_ARROW_LEFT' });
    }
    if (withKeyboard) {
      activeKeyboard();
    }
  };

  const renderArrow = ({ type, onClick, isEdge }) => {
    const pos = type === consts.PREV ? {} : { right: '0' };
    return (
      <ActionButton
        buttonsHeight={buttonsHeight}
        showArrowsWhenDisabled={itemsData.length <= elementsToShow ? false : showArrowsWhenDisabled}
        disabled={isEdge}
        settings={type === consts.PREV ? prevButtonSettings : nextButtonSettings}
        labelKey={type === consts.PREV ? 'cmp-carrousel-prev' : 'cmp-carrousel-next'}
        {...pos}
        onArrowClick={type === consts.PREV ? handlePrev : handleNext}
        dataTestId={type === consts.PREV ? 'carousellBtnPrevious' : 'carousellBtnNext'}
      />
    );
  };

  const renderPagination = ({ pages, activePage, onClick }) => {
    return (
      <Box display={'flex'} flexDirection={'row'} {...dotsContainerSettings}>
        <Text className="fr-sr-only" ariaLive="polite" ariaAtomic="true">
          {`${translate('cmp-carousel-slide', 'Slide')} ${activePage + 1} ${translate(
            'cmp-carousel-of',
            'of'
          )} ${length}`}
        </Text>
        {pages.map((page) => {
          const isActivePage = activePage === page;
          return (
            <Box key={`dot-${page}`} {...reelSettings}>
              <Dot
                {...dotSettings}
                {...activeDotSettings(
                  page,
                  length > maxNumberDots ? dotActive : activePage,
                  dotSettings
                )}
                {...sideDotsSettings(
                  page,
                  dotsVisible,
                  dotSettings,
                  itemsData,
                  elementsToShow,
                  elementsToScroll
                )}
                pageNumber={page}
                isDotVisible={isDotVisible(page, dotsVisible)}
                ariaCurrent={isActivePage}
              />
            </Box>
          );
        })}
      </Box>
    );
  };

  const onResize = (currentBreakPoint) => {
    setBreakPointActive(currentBreakPoint);
  };

  const reactElasticCarouselProps = {
    isRTL: direction === 'rtl' ? true : false,
    initialActiveIndex: 0,
    pagination: showPagination,
    enableAutoPlay: autoplay,
    autoPlaySpeed: speed,
    showArrows: showNavigation,
    autoTabIndexVisibleItems: false,
    renderArrow: renderArrow,
    renderPagination: renderPagination,
    onResize: onResize,
    disableArrowsOnEnd: false,
    itemsToShow: elementsToShow,
    itemsToScroll: elementsToScroll,
    ...recProps
  };

  const onSwipedLeft = () => {
    if (elasticCarouselRef.current?.state.activePage !== dotActive) {
      tracking({ type: `CAROUSEL_SWIPE_LEFT` });
    }
  };

  const onSwipedRight = () => {
    if (elasticCarouselRef.current?.state.activePage !== dotActive) {
      tracking({ type: `CAROUSEL_SWIPE_RIGHT` });
    }
  };

  const { swipeWidth = 45 } = reactElasticCarouselProps;

  const handlers = useSwipeable({
    trackMouse: true,
    onSwipeStart: (SwipeEventData) =>
      SwipeEventData.initial[0] !== 0 && SwipeEventData.initial[1] !== 0
        ? handleDraggingBeforeChange()
        : null,
    onSwipedLeft: onSwipedLeft,
    onSwipedRight: onSwipedRight,
    delta: swipeWidth
  });

  const renderItems = useMemo(
    () =>
      itemsData.map((item, index) => {
        const data = { ...item };
        const key = data.index ? data.index : index;
        data.navigationInfo =
          direction === 'ltr'
            ? `${index + 1}/${
                emptyCards !== elementsToShow ? itemsSize - emptyCardsLenght : itemsSize
              }`
            : `${emptyCards !== elementsToShow ? itemsSize - emptyCardsLenght : itemsSize}/${
                index + 1
              }`;

        const isVisible = currentIndex <= index && currentIndex + elementsToShow > index;
        const render =
          currentIndex + 3 * elementsToShow > index && index + 3 * elementsToShow >= currentIndex;
        return (
          <Col height="100" {...colSettings} key={key} onClickCapture={handleOnItemClick}>
            {!item.noData && render ? (
              <ItemComponentInstance
                {...itemSettings}
                data={data}
                tabIndex={isVisible}
                tabIndexCallToAction={isVisible}
                ariaHidden={!isVisible}
                focusReferences={(ref) => {
                  arrayRef.current[index] = ref;
                }}
                ariaLabel={
                  index === 0
                    ? translate('cmp-carousel-container-explain', 'Carousel. Best fares on flights')
                    : ''
                }
              />
            ) : (
              <div> </div>
            )}
          </Col>
        );
      }),
    [
      colSettings,
      currentIndex,
      direction,
      elementsToShow,
      emptyCards,
      emptyCardsLenght,
      handleOnItemClick,
      itemSettings,
      itemsData,
      itemsSize,
      translate
    ]
  );

  return (
    <Box
      width="100"
      dataTestId="carousel"
      className={`${className} `}
      style={style}
      role={'application'}
      ariaRoleDescription={translate('cmp-carousel-role-description', 'Slider')}
    >
      <div {...handlers}>
        <ElasticCarousel
          {...reactElasticCarouselProps}
          breakPoints={breakPoints}
          showEmptySlots
          ref={(ref) => {
            elasticCarouselRef.current = ref;
          }}
          onChange={(currentItem, pageIndex) => {
            length > maxNumberDots
              ? handleChange(elasticCarouselRef.current?.state.activeIndex, pageIndex)
              : setCurrentIndex(currentItem.index);
            handleDraggingAfterChange();
          }}
        >
          {renderItems}
        </ElasticCarousel>
      </div>
    </Box>
  );
};

Carousel.propTypes = {
  /**
   * Show dots in the component
   */
  showDots: PropTypes.bool,
  /**
   * show Navigation Buttons
   */
  showNavigation: PropTypes.bool,
  /**
   * autoplay
   */
  autoplay: PropTypes.bool,
  /**
   * Prevents autoplay while hovering on track
   */
  pauseOnHover: PropTypes.bool,
  /**
   * How many slides to show in one frame
   */
  elementsToShow: PropTypes.number,
  /**
   * How many slides to scroll at once
   */
  elementsToScroll: PropTypes.number,
  /**
   * Animation speed in milliseconds
   */
  speed: PropTypes.number,
  /**
   * itemSettings object of settings
   */
  itemSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * prevButtonSettings object of settings
   */
  prevButtonSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * nextButtonSettings object of settings
   */
  nextButtonSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * itemSettings object of settings
   */
  dotSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Component
   */
  ItemComponentInstance: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  /**
   * List items
   */
  itemsData: PropTypes.arrayOf(PropTypes.any),
  /**
   * Define if show or hide < > buttons when are disabled
   */
  showArrowsWhenDisabled: PropTypes.bool,
  /**
   * Col settings
   */
  colSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Number of dots to show before reel
   */
  maxNumberDots: PropTypes.number,
  /**
   * * Dots container component settings
   */
  dotsContainerSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Reel component settings
   */
  reelSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Enable or disable keyboard navigation
   */
  enableKeyBoardNavigation: PropTypes.bool,
  /**
   * Ajust the width of card in depedencies of the itemsData.length
   */
  widthCardAuto: PropTypes.bool,
  /**
   * Define height of the cards in the carousel
   */
  cardHeight: PropTypes.string,
  /**
   * Lazy load
   */
  lazyLoad: PropTypes.oneOf(['disabled', 'ondemand', 'progressive']),
  /**
   * Show uistyle style
   */
  uiStyles: PropTypes.shape({
    borderColor: PropTypes.string,
    borderWidth: PropTypes.string,
    rounded: PropTypes.string
  }),
  /**
   * This setting is to allow
   **/
  preventClickOnDragging: PropTypes.bool,
  /**
   * Function that will handle tracking events
   */
  tracking: PropTypes.func,
  /**
   *  Adjust the number of card show in dependencies of the view port
   */
  autoAdjustViewOfComponent: PropTypes.bool,
  paramAutoAdjustViewOfComponent: PropTypes.objectOf(PropTypes.any)
};

Carousel.defaultProps = {
  showDots: true,
  showNavigation: true,
  autoplay: false,
  elementsToShow: 4,
  elementsToScroll: 4,
  maxNumberDots: 10,
  speed: 500,
  itemSettings: {},
  prevButtonSettings: {
    roundedCircle: true,
    lineHeight: '1',
    paddingWidth: '18px',
    paddingHeight: '15px',
    color: 'dark',
    iconProps: {
      icon: 'left-chevron',
      size: 10
    },
    marginLeft: '-10px'
  },
  nextButtonSettings: {
    roundedCircle: true,
    lineHeight: '1',
    paddingWidth: '18px',
    paddingHeight: '15px',
    color: 'dark',
    iconProps: {
      icon: 'right-chevron',
      size: 10
    },
    marginRight: '-10px'
  },
  dotsContainerSettings: {
    marginTop: '10px'
  },
  reelSettings: {
    textAlign: 'center'
  },
  dotSettings: {
    color: 'dark',
    outline: true,
    padding: '4px',
    marginRight: '5px',
    roundedCircle: true,
    dotActiveSetting: {
      outline: false
    },
    sideDots: {
      padding: '2px'
    }
  },
  lazyLoad: 'disabled',
  ItemComponentInstance: null,
  itemsData: [],
  showArrowsWhenDisabled: false,
  colSettings: {
    height: '100'
  },
  enableKeyBoardNavigation: false,
  widthCardAuto: true,
  uiStyles: null,
  cardHeight: null,
  tracking: () => {},
  autoAdjustViewOfComponent: false,
  paramAutoAdjustViewOfComponent: {}
};

Carousel.componentName = 'Carousel';

export default React.memo(
  Carousel,
  (prevProps, nextProps) => prevProps.itemsData === nextProps.itemsData
);
